<script>
import { mapGetters, mapMutations, mapActions } from 'vuex';
import { required } from 'vuelidate/lib/validators';
import utilsMixin from '@shared/mixins/utils';
import responsiveMixin from '@shared/mixins/responsive';
import communityMixin from '@shared/mixins/community';
import TiptapEditor from '@shared/components/TiptapEditor.vue';
import GenericUploadForm from '@shared/components/GenericUploadForm.vue';

export default {
  mixins: [
    utilsMixin,
    communityMixin('post'),
    responsiveMixin({ isMobile: 767 }),
  ],
  components: { TiptapEditor, GenericUploadForm },
  props: {
    group: {
      type: Object,
      required: true,
    },
    post: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      newPost: {
        title: '',
        content: '',
        media: {},
        media_type: null,
        published_at: null,
      },
      uploadType: null,
      uploadModalOpen: false,
      hasScheduleOpen: false,
      scheduleMinDate: new Date(),
      isLoading: false,
    };
  },
  validations: {
    newPost: {
      content: {
        required,
      },
    },
  },
  computed: {
    ...mapGetters('community', [
      'communityStore',
    ]),
    hasTitleField() {
      return ['LIST', 'GRID'].includes(this.group.type);
    },
    acceptedSources() {
      if (['image', 'resource'].includes(this.uploadType)) {
        return ['upload'];
      }

      if (this.uploadType === 'video') {
        return ['embed', 'url'];
      }

      if (this.uploadType === 'audio') {
        return ['url'];
      }

      return null;
    },
    addImageTooltip() {
      return this.$t('add_image_tooltip');
    },
    addVideoTooltip() {
      return this.$t('add_video_tooltip');
    },
    addAttachmentTooltip() {
      return this.$t('add_attachment_tooltip');
    },
    scheduleTooltip() {
      return this.$t('schedule_tooltip');
    },
  },
  methods: {
    ...mapMutations('community', [
      'closePostForm',
    ]),
    ...mapActions('community', [
      'updatePost',
      'addPost',
    ]),
    openScheduler() {
      if (!this.sharedHasExpertPlan) {
        this.$showMessage.goPro(() => {
          this.$router.push({ name: 'plan', query: { onglet: 'vous' } });
        }, null, { plan: 'EXPERT' });
        return;
      }

      this.hasScheduleOpen = true;
    },
    showUploadForm(type) {
      this.uploadType = type;
      this.uploadModalOpen = true;
    },
    onUpload(media) {
      const MEDIA_TYPE_MAP = {
        image: 'IMAGE',
        video: 'EMBED',
        resource: 'RESOURCE',
      };

      this.newPost.media = media;
      this.newPost.media_type = MEDIA_TYPE_MAP[this.uploadType];
      this.uploadType = null;
      this.uploadModalOpen = false;
    },
    removeMedia() {
      this.newPost.media = {};
      this.newPost.media_type = null;
    },
    handle() {
      const data = { ...this.newPost };
      data.published_at = this.$moment(data.published_at || undefined)
        .utc()
        .format(this.$constants.SERVER_DATE_FORMAT);

      data.media == null && (delete data.media);
      data.media_type == null && (delete data.media_type);
      this.hasTitleField === false && (delete data.title);

      this.isLoading = true;
      const promise = this.post
        ? this.updatePost({ uuid: this.post.uuid, data })
        : this.addPost({ uuid: this.group.uuid, data });

      promise
        .then(() => this.closePostForm())
        .finally(() => (this.isLoading = false));
    },
  },
  created() {
    this.hydrateData('newPost', 'post');

    if (this.post) {
      this.newPost.published_at = this.$moment
        .utc(this.newPost.published_at)
        .toDate();
    }
  },
  i18n: {
    messages: {
      en: {
        edit: 'Edit',
        create: 'Create',
        topic: 'a topic',
        publication: 'a post',
        title: 'Title',
        whats_new: "What's new?",
        view_media: 'View media',
        mb: 'MB',
        delete: 'Delete',
        add_image_tooltip: 'Add an image',
        add_video_tooltip: 'Add a video',
        add_attachment_tooltip: 'Add an attachment',
        schedule_tooltip: 'Schedule for later',
        cancel: 'Cancel',
        update: 'Update',
        publish: 'Publish',
      },
      fr: {
        edit: 'Modifier',
        create: 'Créer',
        topic: 'un sujet',
        publication: 'une publication',
        title: 'Titre',
        whats_new: 'Quoi de neuf ?',
        view_media: 'Voir le média',
        mb: 'Mo',
        delete: 'Supprimer',
        add_image_tooltip: 'Ajouter une image',
        add_video_tooltip: 'Ajouter une vidéo',
        add_attachment_tooltip: 'Ajouter une pièce jointe',
        schedule_tooltip: 'Programmer pour plus tard',
        cancel: 'Annuler',
        update: 'Mettre à jour',
        publish: 'Publier',
      },
    },
  },
};
</script>

<template>
  <AppCardModal
    tag="form"
    @submit.prevent="dataIsValid(isLoading) && handle()"
  >
    <template #title>
      <span>{{ $t(post ? 'edit' : 'create') }} {{ $t(group.type === 'LIST' ? 'topic' : 'publication') }}</span>
    </template>
    <template #body>
      <b-field v-if="hasTitleField" :label="$t('title')">
        <b-input
          v-model="newPost.title"
          maxlength="150"
          required
        />
      </b-field>
      <b-field v-bind="$getErrorProps($v.newPost.content, ['required'])">
        <TiptapEditor
          :hidden="['h2', 'table']"
          :height="120"
          :placeholder="$t('whats_new')"
          autoFocus
          v-model="newPost.content"
        />
      </b-field>

      <div v-if="newPost.media.url" class="box flex-between-center">
        <a
          class="tdecoration-underline"
          :href="newPost.media.url"
          target="_blank"
          rel="noreferrer noopener"
        >
          {{ $t('view_media') }}
          <template v-if="newPost.media.type === 'upload'">
            ({{ newPost.media.size | formatSizeToMB }} {{ $t('mb') }})
          </template>
        </a>

        <b-button
          class="has-text-danger"
          type="is-text"
          @click="removeMedia"
        >
          {{ $t('delete') }}
        </b-button>
      </div>

      <teleport to="modals">
        <b-modal
          v-if="uploadModalOpen"
          :active="uploadModalOpen"
          :canCancel="false"
          :fullScreen="isMobile"
          hasModalCard
        >
          <GenericUploadForm
            :type="uploadType"
            :storeUUID="communityStore.uuid"
            :acceptedSources="acceptedSources"
            contextType="GROUP"
            :contextId="group.uuid"
            @cancel="uploadModalOpen = false"
            @done="onUpload"
          />
        </b-modal>
      </teleport>
    </template>
    <template #foot>
      <div class="level w-full">
        <div class="level-left">
          <div class="is-flex is-flex-wrap-wrap">
            <b-tooltip position="is-right" :label="addImageTooltip">
              <b-button
                type="is-text"
                icon-left="image"
                @click="showUploadForm('image')"
              />
            </b-tooltip>
            <b-tooltip position="is-right" :label="addVideoTooltip">
              <b-button
                type="is-text"
                icon-left="video"
                @click="showUploadForm('video')"
              />
            </b-tooltip>
            <b-tooltip position="is-right" :label="addAttachmentTooltip">
              <b-button
                type="is-text"
                icon-left="file"
                @click="showUploadForm('resource')"
              />
            </b-tooltip>
            <b-field v-if="canManageCommunityEntity && !post" grouped>
              <p class="control">
                <b-tooltip
                  position="is-right"
                  :label="scheduleTooltip"
                  :active="!hasScheduleOpen"
                >
                  <b-button
                    type="is-text"
                    icon-left="calendar"
                    @click="openScheduler"
                  />
                </b-tooltip>
              </p>
              <b-datetimepicker
                v-show="hasScheduleOpen"
                v-model="newPost.published_at"
                :min-datetime="scheduleMinDate"
                :datepicker="{ nearbyMonthDays: false }"
                :datetime-formatter="
                  (date) => $moment(date).format('DD/MM/YYYY, HH:mm')
                "
                position="is-top-right"
                close
                append-to-body
              />
            </b-field>
          </div>
        </div>
        <div class="level-right has-text-right">
          <nav>
            <b-button type="is-text" @click="$emit('cancel')">
              {{ $t('cancel') }}
            </b-button>
            <b-button
              type="is-primary is-custom"
              native-type="submit"
              :loading="isLoading"
            >
              {{ post ? $t('update') : $t('publish') }}
            </b-button>
          </nav>
        </div>
      </div>
    </template>
  </AppCardModal>
</template>
